import React, { useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Typography, IconButton, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodePollHistory — Expandable poll activity list with status icons and response bodies.
 * Used by: API Polling, Await External Event.
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const List = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  overflow: 'hidden',
}));

const PollItem = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': { borderBottom: 'none' },
}));

const PollHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 8px',
  cursor: 'pointer',
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

const StatusIcon = ({ status }) => {
  if (status === 'success') return <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />;
  if (status === 'error') return <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />;
  return <HourglassEmptyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />;
};

const PollTimestamp = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  flex: 1,
}));

const PollStatus = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  fontWeight: NODE_UI.fontWeight.label,
}));

const PollBody = styled('div')(({ theme }) => ({
  padding: '8px 10px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafafa',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ResponseText = styled('pre')(({ theme }) => ({
  fontSize: 11,
  fontFamily: 'monospace',
  color: theme.palette.text.primary,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  margin: 0,
  maxHeight: 200,
  overflowY: 'auto',
}));

const EmptyState = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  padding: '16px 8px',
}));

const NodePollHistory = React.memo(function NodePollHistory({
  label = 'Poll History',
  polls = [],
  emptyMessage = 'No poll activity yet',
  sx,
}) {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const handleToggle = useCallback((index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  }, []);

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <List sx={sx}>
        {polls.length === 0 ? (
          <EmptyState>{emptyMessage}</EmptyState>
        ) : (
          polls.map((poll, index) => (
            <PollItem key={poll.id || index}>
              <PollHeader onClick={() => handleToggle(index)}>
                <StatusIcon status={poll.status} />
                <PollTimestamp>{poll.timestamp || `Poll #${index + 1}`}</PollTimestamp>
                <PollStatus sx={{ color: poll.status === 'success' ? 'success.main' : poll.status === 'error' ? 'error.main' : 'text.secondary' }}>
                  {poll.statusCode || poll.status}
                </PollStatus>
                <IconButton size="small" sx={{ p: '2px', transform: expandedIndex === index ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                  <ExpandMoreIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </PollHeader>
              <Collapse in={expandedIndex === index}>
                <PollBody>
                  <ResponseText>
                    {typeof poll.response === 'string' ? poll.response : JSON.stringify(poll.response, null, 2)}
                  </ResponseText>
                </PollBody>
              </Collapse>
            </PollItem>
          ))
        )}
      </List>
    </div>
  );
});

export default NodePollHistory;
